import { Button } from '@/components/ui/Button';
import { SectionCard } from '@/components/common/SectionCard';
import { RENEWAL_STRINGS as T } from '@/lib/constants/renewal-strings';
import type { RenewalOffer } from '@/lib/types/billing';
import { formatDate } from '@/lib/utils/date.utils';
import { formatCents } from '@/lib/utils/format.utils';

export interface RenewalOfferPanelProps {
  /** The startup's offer; nothing renders unless it is open with at least one plan. */
  offer: RenewalOffer | undefined;
  onSubscribe: (planId: string) => void;
  /** The plan whose checkout is opening, so its button shows progress and the rest wait. */
  pendingPlanId: string | null;
}

/**
 * The data room renewal offer (MYN-247, FR-255, FR-295) on the Legal Command
 * Center and on Billing & Info: when the package ends or ended, how long the
 * documents are kept, and each renewal plan with its price. Presentational:
 * each page supplies the offer and what Subscribe does.
 */
export function RenewalOfferPanel({ offer, onSubscribe, pendingPlanId }: RenewalOfferPanelProps) {
  if (!offer?.eligible || offer.plans.length === 0) return null;
  const body = offer.premiumOpen
    ? T.WHILE_OPEN(formatDate(offer.premiumEndsAt))
    : T.AFTER_END(formatDate(offer.premiumEndsAt), formatDate(offer.offerClosesAt));

  return (
    <SectionCard title={T.TITLE}>
      <div className="flex flex-col gap-4">
        <p className="text-text-secondary text-sm">{body}</p>
        <ul className="flex flex-col gap-3">
          {offer.plans.map((plan) => (
            <li
              key={plan.id}
              className="border-border-subtle flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
            >
              <span className="text-text-primary text-sm font-medium">
                {plan.name} - {formatCents(plan.priceCents)}
                {T.PER_INTERVAL[plan.billingInterval]}
              </span>
              <Button
                variant="primary"
                onClick={() => onSubscribe(plan.id)}
                disabled={pendingPlanId !== null}
              >
                {pendingPlanId === plan.id ? T.SUBSCRIBING : T.SUBSCRIBE}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </SectionCard>
  );
}
